import React, { useState, useRef, useEffect } from "react";
import { useForm } from "@formspree/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Mail, Menu, X, Calendar, CreditCard, RotateCcw, Droplet, Leaf,
  Venus, Soup, HeartPulse, Stethoscope, Linkedin, Instagram, ChevronDown,
  ArrowLeft, ArrowRight, Clock, User, Tag, Check, CheckCircle2, ClipboardList,
  GlassWater, Wheat, Dumbbell, Apple, Utensils, ShieldCheck, LogOut,
  Scale, Syringe, ChevronUp, Music2
} from "lucide-react";
import "./App.css";
import "./index.css";
import { useTranslation } from "react-i18next";
import IntestineIcon from "./Icons/IntestineIcon";
import { Analytics } from "@vercel/analytics/react"
import { track } from "@vercel/analytics";
import { blogPosts, getBlogPost } from "./blogPosts";
import { localizeBlogPosts } from "./blogContent.es";
import { servicesContent } from "./servicesData";
import { localizeAssessmentSteps } from "./assessmentContent";
import { getUiContent } from "./uiContent";
import {
  assessmentSteps,
  calculateAssessmentResults,
  getCompletedQuestionCount,
  getQuestionCount
} from "./nutritionAssessment";
import { isSupabaseConfigured, supabase } from "./supabaseClient";
import {
  clearAssessmentDraft,
  createAssessmentLead,
  getCurrentSession,
  isCurrentUserAdmin,
  listSubscribers,
  loadAssessmentDraft,
  saveAssessmentDraft,
  saveAssessmentResponse,
  signInAdmin,
  signOutAdmin,
  subscribeFromBlog,
  updateUnsubscribeStatus
} from "./submissionService";

// Language Dropdown Component
function LanguageDropdown() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const languages = [
    { code: "en", label: "English", flag: "/en.png" },
    { code: "es", label: "Español", flag: "/es.png" }
  ];

  return (
    <div className="relative inline-block shrink-0 text-left">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-2 bg-white rounded-md shadow hover:bg-gray-100 transition sm:gap-2 sm:px-3"
      >
        <img
          src={`/${i18n.language}.png`}
          alt={i18n.language}
          className="w-5 h-5 rounded"
        />
        <span className="text-sm font-medium capitalize">{i18n.language}</span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg z-10">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                window.localStorage.setItem("nutritionByIballa.language", lang.code);
                i18n.changeLanguage(lang.code);
                setOpen(false);
              }}
              className="flex items-center w-full px-3 py-2 hover:bg-gray-100"
            >
              <img src={lang.flag} alt={lang.label} className="w-5 h-5 rounded mr-2" />
              <span className="text-sm">{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const serviceIcons = {
  clipboard: ClipboardList,
  droplet: Droplet,
  gut: IntestineIcon,
  scale: Scale,
  syringe: Syringe,
  venus: Venus
};

function formatPostDate(date, language = "en") {
  return new Intl.DateTimeFormat(language.startsWith("es") ? "es-ES" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(new Date(date));
}

function upsertMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertPropertyMeta(property, content) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(pathname) {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", `${window.location.origin}${pathname}`);
}

function upsertStructuredData(data) {
  let tag = document.getElementById("nutrition-by-iballa-structured-data");
  if (!tag) {
    tag = document.createElement("script");
    tag.id = "nutrition-by-iballa-structured-data";
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function trackEvent(name, properties = {}) {
  try {
    track(name, properties);
  } catch {
    // Analytics should never interrupt booking or enquiry actions.
  }
}

const calendlyUrls = {
  enFreeCall: "https://calendly.com/nutritionbyiballa/free-15-min-call",
  esFreeCall: "https://calendly.com/nutritionbyiballa/llamada-introductoria-gratuita-de-15-minutos"
};

function NewsletterConsentChoice({ value, onChange, copy, name }) {
  const choices = [
    { value: true, label: copy.signupButton || copy.consent },
    { value: false, label: copy.noConsent }
  ];

  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">{copy.consentHeading}</legend>
      {choices.map((choice) => {
        const selected = value === choice.value;
        return (
          <label
            key={String(choice.value)}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm leading-relaxed transition ${
              selected
                ? "border-[#5ca88d] bg-[#d9f4cd] text-[#294b43] shadow"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:border-[#5ca88d]"
            }`}
          >
            <input
              type="checkbox"
              name={name}
              checked={selected}
              onChange={() => onChange(choice.value)}
              className="sr-only"
            />
            <span
              className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                selected ? "border-[#5ca88d] bg-[#5ca88d] text-white" : "border-[#5ca88d] bg-white"
              }`}
              aria-hidden="true"
            >
              {selected && <Check size={14} strokeWidth={3} />}
            </span>
            <span>{choice.label}</span>
          </label>
        );
      })}
    </fieldset>
  );
}

function NewsletterSignupForm({ compact = false, requireConsent = true, source = "blog" }) {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).newsletter;
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", consent: true });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    if (!form.firstName.trim() || !form.lastName.trim() || !isValidEmail(form.email)) {
      setStatus({ type: "error", message: copy.detailsError });
      return;
    }

    if (requireConsent && !form.consent) {
      setStatus({ type: "success", message: copy.noConsentSuccess });
      return;
    }

    setSubmitting(true);
    try {
      await subscribeFromBlog({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        source
      });
      setForm({ firstName: "", lastName: "", email: "", consent: true });
      setStatus({ type: "success", message: copy.success });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white overflow-hidden ${compact ? "lg:h-full lg:flex lg:flex-col" : ""}`}>
      <div className={`bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-5 text-white ${compact ? "py-5" : "py-6 sm:px-8"}`}>
        <h2 id="newsletter-signup-heading" className={`${compact ? "text-xl leading-tight sm:text-2xl lg:text-[1.65rem]" : "text-2xl sm:text-3xl"} font-semibold`}>
          {copy.heading}
        </h2>
        <p className="mt-2 text-sm sm:text-base leading-relaxed text-white/95">
          {copy.intro}
        </p>
      </div>
      <form className={`${compact ? "space-y-3 p-4 lg:flex-1" : "space-y-4 p-5 sm:p-8"}`} onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.firstName}</span>
            <input
              type="text"
              value={form.firstName}
              onChange={(event) => updateField("firstName", event.target.value)}
              autoComplete="given-name"
              className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.lastName}</span>
            <input
              type="text"
              value={form.lastName}
              onChange={(event) => updateField("lastName", event.target.value)}
              autoComplete="family-name"
              className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
              required
            />
          </label>
        </div>
        <div>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.email}</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              autoComplete="email"
              className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
              required
            />
          </label>
        </div>
        {requireConsent && (
          <NewsletterConsentChoice
            value={form.consent}
            onChange={(value) => updateField("consent", value)}
            copy={copy}
            name={`newsletter-consent-${source}`}
          />
        )}
        {status.message && (
          <p className={`text-sm font-semibold ${status.type === "success" ? "text-[#5ca88d]" : "text-red-600"}`}>
            {status.message}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? copy.saving : copy.subscribe}
          <Mail size={18} />
        </button>
      </form>
    </div>
  );
}

function FooterNewsletterSignup() {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).newsletter;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    if (!isValidEmail(email)) {
      setStatus({ type: "error", message: copy.emailError });
      return;
    }

    setSubmitting(true);
    try {
      await subscribeFromBlog({
        firstName: "",
        lastName: "",
        email,
        source: "homepage"
      });
      setEmail("");
      setStatus({ type: "success", message: copy.shortSuccess });
    } catch (error) {
      setStatus({ type: "error", message: i18n.language.startsWith("es") ? getUiContent("es").common.genericError : error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-b border-[#d9f4cd] bg-white px-4 py-6 sm:px-8 lg:px-12 xl:py-8" aria-labelledby="footer-newsletter-heading">
      <div className="mx-auto flex max-w-screen-xl flex-col gap-4 lg:flex-row lg:items-center lg:justify-between xl:max-w-screen-2xl">
        <div className="max-w-2xl">
          <h2 id="footer-newsletter-heading" className="text-xl font-semibold text-[#294b43] lg:text-2xl">
            {copy.footerHeading}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-[#47645d] lg:text-base">
            {copy.footerIntro}
          </p>
        </div>
        <div className="w-full lg:max-w-2xl">
          <form className="flex flex-col gap-2 sm:flex-row" onSubmit={handleSubmit} noValidate>
            <label className="sr-only" htmlFor="footer-newsletter-email">{copy.emailAddress}</label>
            <input
              id="footer-newsletter-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              placeholder={copy.emailAddress}
              aria-describedby={status.message ? "footer-newsletter-status" : undefined}
              className="h-11 min-w-0 flex-1 rounded-full border border-[#d9f4cd] bg-white px-4 text-gray-900 placeholder:text-gray-500 focus:border-[#5ca88d] focus:outline-none focus:ring-2 focus:ring-[#d9f4cd] lg:h-12 lg:px-5 lg:text-base"
              required
            />
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-11 shrink-0 items-center justify-center rounded-full bg-[#5ca88d] px-6 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 lg:h-12 lg:px-8 lg:text-base"
            >
              {submitting ? copy.subscribing : copy.subscribe}
            </button>
          </form>
          {status.message && (
            <p
              id="footer-newsletter-status"
              role="status"
              className={`mt-2 text-sm font-semibold ${
                status.type === "success" ? "text-[#5ca88d]" : "text-red-700"
              }`}
            >
              {status.message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

const privacyPolicySections = [
  {
    title: "1. Information We Collect",
    body: [
      "We may collect the following types of information:",
      "Personal information you provide: name, email address, phone number, health and dietary information when booking consultations, and any other information you voluntarily submit via forms, emails, subscriptions and appointments.",
      "Non-personal information automatically collected: IP address, browser type, device information, date and time of access, pages visited on our website, and referring website or search engine."
    ]
  },
  {
    title: "2. How We Use Your Information",
    body: [
      "We use your information to provide our nutrition consultation services and book appointments, communicate with you about your health plan or inquiries, send updates, promotions, and newsletters only if you opt in, improve our website, services, and user experience, and ensure security and prevent fraud or misuse of our website."
    ]
  },
  {
    title: "3. Sharing Your Information",
    body: [
      "Third-party service providers: we may share information with providers who help us run our services.",
      "Legal compliance: we may disclose your information if required by law or to protect our legal rights.",
      "We do not sell or rent your personal information to third parties for marketing purposes."
    ]
  },
  {
    title: "4. Cookies",
    body: [
      "We use cookies and similar technologies to improve website functionality, analyze traffic, and deliver personalized content.",
      "You can manage or block cookies through your browser settings.",
      "Google Analytics is used for anonymous traffic analysis."
    ]
  },
  {
    title: "5. Data Security",
    body: [
      "We implement appropriate technical and organizational measures to protect your personal information.",
      "No method of transmission over the internet or electronic storage is 100% secure; any data you provide is at your own risk."
    ]
  },
  {
    title: "6. Your Rights",
    body: [
      "You have the right to access the personal data we hold about you, correct or update inaccurate information, request the deletion of your personal data, and withdraw consent for marketing communications at any time.",
      "To exercise your rights, contact us at nutritionbyiballa@gmail.com. We will respond within 30 days of receiving a valid request."
    ]
  },
  {
    title: "7. Third-Party Links",
    body: [
      "Our website may contain links to external websites. We are not responsible for their content or privacy practices. Please review their privacy policies before submitting personal information."
    ]
  },
  {
    title: "8. Children’s Privacy",
    body: [
      "Our website and services are not intended for individuals under 18 years old. We do not knowingly collect personal information from children."
    ]
  },
  {
    title: "9. Updates to this Privacy Policy",
    body: [
      "We may update this Privacy Policy from time to time. The most recent version will always be available on our website."
    ]
  },
  {
    title: "Contact Us",
    body: [
      "If you have questions or concerns about this Privacy Policy, please contact us at nutritionbyiballa@gmail.com."
    ]
  }
];

const privacyPolicySectionsEs = [
  {
    title: "1. Información que recopilamos",
    body: [
      "Podemos recopilar los siguientes tipos de información:",
      "Información personal que nos facilitas: nombre, dirección de correo electrónico, número de teléfono, información de salud y alimentación al reservar consultas, y cualquier otra información que envíes voluntariamente mediante formularios, correos electrónicos, suscripciones y citas.",
      "Información no personal recopilada automáticamente: dirección IP, tipo de navegador, información del dispositivo, fecha y hora de acceso, páginas visitadas en nuestro sitio web y sitio web o buscador de referencia."
    ]
  },
  {
    title: "2. Cómo usamos tu información",
    body: [
      "Usamos tu información para prestar nuestros servicios de consulta nutricional y gestionar reservas, comunicarnos contigo sobre tu plan de salud o tus consultas, enviar actualizaciones, promociones y boletines solo si das tu consentimiento, mejorar nuestro sitio web, nuestros servicios y la experiencia de usuario, y garantizar la seguridad y prevenir el fraude o el uso indebido de nuestro sitio web."
    ]
  },
  {
    title: "3. Compartir tu información",
    body: [
      "Proveedores externos de servicios: podemos compartir información con proveedores que nos ayudan a prestar nuestros servicios.",
      "Cumplimiento legal: podemos divulgar tu información si lo exige la ley o para proteger nuestros derechos legales.",
      "No vendemos ni alquilamos tu información personal a terceros con fines de marketing."
    ]
  },
  {
    title: "4. Cookies",
    body: [
      "Usamos cookies y tecnologías similares para mejorar el funcionamiento del sitio web, analizar el tráfico y ofrecer contenido personalizado.",
      "Puedes gestionar o bloquear las cookies desde la configuración de tu navegador.",
      "Google Analytics se utiliza para el análisis anónimo del tráfico."
    ]
  },
  {
    title: "5. Seguridad de los datos",
    body: [
      "Aplicamos medidas técnicas y organizativas adecuadas para proteger tu información personal.",
      "Ningún método de transmisión por internet o almacenamiento electrónico es 100 % seguro; cualquier dato que proporciones se facilita bajo tu propio riesgo."
    ]
  },
  {
    title: "6. Tus derechos",
    body: [
      "Tienes derecho a acceder a los datos personales que conservamos sobre ti, corregir o actualizar información inexacta, solicitar la eliminación de tus datos personales y retirar tu consentimiento para comunicaciones de marketing en cualquier momento.",
      "Para ejercer tus derechos, contacta con nosotros en nutritionbyiballa@gmail.com. Responderemos en un plazo de 30 días desde la recepción de una solicitud válida."
    ]
  },
  {
    title: "7. Enlaces de terceros",
    body: [
      "Nuestro sitio web puede contener enlaces a sitios web externos. No somos responsables de su contenido ni de sus prácticas de privacidad. Revisa sus políticas de privacidad antes de enviar información personal."
    ]
  },
  {
    title: "8. Privacidad de menores",
    body: [
      "Nuestro sitio web y nuestros servicios no están dirigidos a personas menores de 18 años. No recopilamos de forma consciente información personal de menores."
    ]
  },
  {
    title: "9. Actualizaciones de esta Política de privacidad",
    body: [
      "Podemos actualizar esta Política de privacidad ocasionalmente. La versión más reciente estará siempre disponible en nuestro sitio web."
    ]
  },
  {
    title: "Contacto",
    body: [
      "Si tienes preguntas o inquietudes sobre esta Política de privacidad, contacta con nosotros en nutritionbyiballa@gmail.com."
    ]
  }
];

function PrivacyPolicyPage() {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language.startsWith("es");
  const sections = isSpanish ? privacyPolicySectionsEs : privacyPolicySections;
  const title = isSpanish ? "Política de privacidad" : "Privacy Policy";
  const lastUpdated = isSpanish ? "Última actualización: febrero de 2026" : "Last updated: February 2026";
  const intro = isSpanish
    ? "Nutrition by Iballa se compromete a proteger tu privacidad y tu información personal. Esta Política de privacidad explica cómo recopilamos, usamos y protegemos tus datos cuando visitas o utilizas nuestro sitio web y nuestros servicios online. Al utilizar nuestro sitio web, aceptas las prácticas descritas en esta política."
    : "Nutrition by Iballa is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you visit or use our website and online services. By using our website, you consent to the practices described in this policy.";

  return (
    <main className="bg-gray-50 text-gray-900">
      <section className="pt-10 pb-10 text-center bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] text-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold uppercase tracking-wide mb-3">Nutrition by Iballa</p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-tight">
            {title}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            {lastUpdated}
          </p>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 md:px-12">
          <article className="rounded-xl bg-white p-5 shadow-lg ring-1 ring-[#5ca88d] sm:p-8">
            <p className="text-base leading-relaxed text-gray-700">
              {intro}
            </p>

            <div className="mt-8 space-y-8">
              {sections.map((section) => (
                <section key={section.title}>
                  <h2 className="text-xl font-semibold text-[#294b43]">{section.title}</h2>
                  <div className="mt-3 space-y-3">
                    {section.body.map((paragraph) => (
                      <p key={paragraph} className="text-base leading-relaxed text-gray-700">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}

const cookiePolicySections = [
  {
    title: "1. What Cookies Are",
    body: [
      "Cookies are small text files placed on your device when you visit a website. They help websites work properly, remember preferences, understand how visitors use pages, and support third-party tools."
    ]
  },
  {
    title: "2. Cookies We Use",
    body: [
      "Nutrition by Iballa may use essential cookies needed for the website to function, analytics cookies to understand general website use, and third-party cookies from services such as Calendly when you use booking links.",
      "Calendly manages its own booking cookies and privacy controls. Please review Calendly's own policies when booking an appointment."
    ]
  },
  {
    title: "3. Managing Cookies",
    body: [
      "You can manage, block or delete cookies through your browser settings. Some website features may not work as expected if essential cookies are disabled."
    ]
  },
  {
    title: "4. Contact",
    body: [
      "If you have questions about this Cookie Policy, contact nutritionbyiballa@gmail.com."
    ]
  }
];

const cookiePolicySectionsEs = [
  {
    title: "1. Qué son las cookies",
    body: [
      "Las cookies son pequeños archivos de texto que se guardan en tu dispositivo cuando visitas un sitio web. Ayudan a que las páginas funcionen correctamente, recuerden preferencias, comprendan cómo se usan y permitan el funcionamiento de herramientas externas."
    ]
  },
  {
    title: "2. Cookies que utilizamos",
    body: [
      "Nutrition by Iballa puede usar cookies esenciales necesarias para el funcionamiento del sitio web, cookies de análisis para comprender el uso general de la web y cookies de terceros de servicios como Calendly cuando utilizas los enlaces de reserva.",
      "Calendly gestiona sus propias cookies de reserva y controles de privacidad. Revisa las políticas de Calendly al reservar una cita."
    ]
  },
  {
    title: "3. Gestionar cookies",
    body: [
      "Puedes gestionar, bloquear o eliminar cookies desde la configuración de tu navegador. Algunas funciones del sitio web pueden no funcionar correctamente si desactivas cookies esenciales."
    ]
  },
  {
    title: "4. Contacto",
    body: [
      "Si tienes preguntas sobre esta Política de cookies, contacta con nutritionbyiballa@gmail.com."
    ]
  }
];

function CookiePolicyPage() {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language.startsWith("es");
  const sections = isSpanish ? cookiePolicySectionsEs : cookiePolicySections;
  const title = isSpanish ? "Política de cookies" : "Cookie Policy";
  const lastUpdated = isSpanish ? "Última actualización: junio de 2026" : "Last updated: June 2026";
  const intro = isSpanish
    ? "Esta Política de cookies explica cómo Nutrition by Iballa utiliza cookies y tecnologías similares en este sitio web."
    : "This Cookie Policy explains how Nutrition by Iballa uses cookies and similar technologies on this website.";

  return (
    <main className="bg-gray-50 text-gray-900">
      <section className="pt-10 pb-10 text-center bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] text-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold uppercase tracking-wide mb-3">Nutrition by Iballa</p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-tight">
            {title}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            {lastUpdated}
          </p>
        </div>
      </section>

      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 md:px-12">
          <article className="rounded-xl bg-white p-5 shadow-lg ring-1 ring-[#5ca88d] sm:p-8">
            <p className="text-base leading-relaxed text-gray-700">
              {intro}
            </p>
            <div className="mt-8 space-y-8">
              {sections.map((section) => (
                <section key={section.title}>
                  <h2 className="text-xl font-semibold text-[#294b43]">{section.title}</h2>
                  <div className="mt-3 space-y-3">
                    {section.body.map((paragraph) => (
                      <p key={paragraph} className="text-base leading-relaxed text-gray-700">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}

const beyondInjectionUrl = "https://iballa-s-site.thinkific.com/products/courses/Beyondtheinjection";
const beyondInjectionImage = "/BTIImage.png";

const beyondInclusions = [
  {
    title: "12 Recorded Nutrition Webinars",
    text: "Structured nutrition education that participants can work through during the programme.",
    icon: ClipboardList
  },
  {
    title: "4 Private Consultations",
    text: "Individual one-to-one dietetic support with Iballa at key points in the 12 weeks.",
    icon: User
  },
  {
    title: "3 Live Q&A Sessions",
    text: "Opportunities to ask questions and receive additional guidance as you put the learning into practice.",
    icon: Mail
  },
  {
    title: "Bonus Webinars",
    text: "Additional educational content to deepen your understanding of nutrition alongside GLP-1 treatment.",
    icon: CheckCircle2
  },
  {
    title: "Community Access",
    text: "Ongoing support throughout the programme so you are not working through the process alone.",
    icon: HeartPulse
  }
];

function BeyondInjectionCta({ location, children, variant = "primary", className = "" }) {
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const toneClass = variant === "light"
    ? "bg-white text-[#5ca88d] hover:bg-gray-100"
    : "bg-[#5ca88d] text-white hover:opacity-90";

  return (
    <a
      href={beyondInjectionUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("beyond_injection_cta_clicked", { language: lang, location })}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-semibold shadow transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2 lg:px-8 lg:py-3 lg:text-base ${toneClass} ${className}`}
    >
      {children}
      <ArrowRight size={18} className="text-[#ffb3b3]" aria-hidden="true" />
    </a>
  );
}

function BeyondInjectionPromo({ embedded = false }) {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith("es");
  const headingId = embedded ? "beyond-blog-heading" : "beyond-home-heading";
  const programmeLabel = isSpanish ? "Descubre el programa" : "Discover the Programme";

  return (
    <section className={embedded ? "mb-10" : "bg-[#ffb3b3] px-4 py-12 sm:px-8 sm:py-16 lg:px-16 lg:py-20 xl:px-20"} aria-labelledby={headingId}>
      <div className="mx-auto grid max-w-screen-xl items-center gap-8 overflow-hidden rounded-2xl bg-white p-5 shadow-lg ring-1 ring-[#5ca88d] md:grid-cols-[minmax(0,1fr)_minmax(18rem,0.85fr)] sm:p-8 lg:gap-12 lg:p-10 xl:max-w-screen-2xl">
        <div>
          <h2 id={headingId} className="text-3xl font-semibold leading-tight text-[#294b43] sm:text-4xl lg:text-5xl">
            Beyond the Injection
          </h2>
          <p className="mt-3 text-lg font-semibold text-[#5ca88d]">{isSpanish ? "Programa de nutrición GLP-1 de 12 semanas" : "12-week GLP-1 nutrition programme"}</p>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-gray-700 lg:text-lg">
            {isSpanish ? "Un programa estructurado con educación nutricional, apoyo dietético personalizado y orientación práctica para personas que utilizan medicamentos GLP-1." : "A structured programme with nutrition education, personalised dietetic support and practical guidance for people using GLP-1 medication."}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {(isSpanish ? ["12 seminarios online", "4 consultas", "3 sesiones de preguntas en directo", "Acceso a la comunidad"] : ["12 webinars", "4 consultations", "3 live Q&As", "Community access"]).map((item) => (
              <span key={item} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#5ca88d] ring-1 ring-[#d9f4cd]">
                {item}
              </span>
            ))}
          </div>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
            <p className="text-2xl font-bold text-[#294b43]">€399</p>
            <a
              href="/beyond-the-injection"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2 lg:px-8 lg:py-3 lg:text-base"
            >
              {programmeLabel}
              <ArrowRight size={18} className="text-[#ffb3b3]" aria-hidden="true" />
            </a>
          </div>
        </div>
        <a href="/beyond-the-injection" aria-label={programmeLabel} className="flex min-h-64 items-center justify-center rounded-xl bg-[#d9f4cd] p-3 shadow-md transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2">
          <img
            src={beyondInjectionImage}
            alt={isSpanish ? "Beyond the Injection, programa de 12 semanas de Iballa, dietista-nutricionista" : "Beyond the Injection 12-week programme by Iballa, registered dietitian"}
            className="max-h-[28rem] w-full rounded-lg object-contain"
          />
        </a>
      </div>
    </section>
  );
}

function ServicesPage({ title, serviceCopy, expandedServiceKey, setExpandedServiceKey, lang }) {
  const getServiceBookingHref = (serviceKey) => (
    serviceKey === "glp1-support" ? "/beyond-the-injection" : "/booking"
  );

  return (
    <main className="bg-white text-[#294b43]">
      <section className="bg-[#d9f4cd] px-5 py-10 sm:px-8 sm:py-12 lg:px-16 lg:py-14 xl:px-20">
        <div className="mx-auto max-w-screen-xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#5ca88d]">
            Nutrition by Iballa
          </p>
          <h1 className="text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          {serviceCopy.intro && (
            <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-[#294b43] sm:text-lg">
              {serviceCopy.intro}
            </p>
          )}
        </div>
      </section>

      <section id="services" className="bg-white px-5 py-10 text-[#294b43] sm:px-8 sm:py-12 lg:px-16 lg:py-14 xl:px-20">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-3 min-[430px]:grid-cols-2 md:gap-5 xl:gap-8">
          {serviceCopy.services.map((service) => {
            const Icon = serviceIcons[service.icon];
            const isExpanded = expandedServiceKey === service.key;
            const panelId = `service-panel-${service.key}`;
            return (
              <motion.article
                key={service.key}
                layout
                transition={{ layout: { duration: 0.25, ease: "easeOut" } }}
                className={`min-h-0 overflow-hidden rounded-2xl border bg-white transition-[box-shadow,border-color] duration-300 ${
                  isExpanded
                    ? "min-[430px]:col-span-2 border-[#ffb3b3] shadow-[0_12px_36px_rgba(59,95,88,0.14)]"
                    : "border-[#ffb3b3] shadow-[0_8px_30px_rgba(59,95,88,0.09)] hover:shadow-[0_12px_36px_rgba(59,95,88,0.14)]"
                }`}
              >
                <div>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    aria-label={`${isExpanded ? serviceCopy.readLess : serviceCopy.readMore}: ${service.title}`}
                    onClick={() => setExpandedServiceKey(isExpanded ? null : service.key)}
                    className="group relative block w-full p-3 pr-11 text-left transition-colors hover:bg-[#d9f4cd] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#ffb3b3] sm:p-5 sm:pr-14 md:p-6 md:pr-16 xl:p-8 xl:pr-20"
                  >
                    <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#d9f4cd] text-[#ffb3b3] transition sm:right-5 sm:top-5 xl:right-8 xl:top-8 xl:h-10 xl:w-10">
                      {isExpanded ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
                    </span>
                    <span className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4 xl:gap-6">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#d9f4cd] text-[#ffb3b3] sm:h-12 sm:w-12 xl:h-16 xl:w-16">
                        <Icon size={23} className="sm:h-[27px] sm:w-[27px] xl:h-9 xl:w-9" aria-hidden="true" />
                      </span>
                      <span>
                        <span role="heading" aria-level="2" className="block text-[15px] font-semibold leading-snug text-[#294b43] sm:text-lg md:text-xl xl:text-2xl">{service.title}</span>
                      </span>
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        id={panelId}
                        role="region"
                        aria-label={service.title}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ height: { duration: 0.25, ease: "easeOut" }, opacity: { duration: 0.18 } }}
                        className="overflow-hidden px-3 pb-3 sm:px-5 sm:pb-5 md:px-6 md:pb-6 xl:px-8 xl:pb-8"
                      >
                        <div className="border-t border-[#d9f4cd] pt-4 sm:pt-5">
                          <div className="grid gap-4 md:grid-cols-3 md:gap-5 xl:gap-8">
                            <div>
                              <h3 className="font-semibold text-[#5ca88d] xl:text-lg">{serviceCopy.headings.what}</h3>
                              <p className="mt-2 text-sm leading-relaxed text-gray-700 xl:text-base">{service.what}</p>
                            </div>
                            <div>
                              <h3 className="font-semibold text-[#5ca88d] xl:text-lg">{serviceCopy.headings.who}</h3>
                              <p className="mt-2 text-sm leading-relaxed text-gray-700 xl:text-base">{service.who}</p>
                            </div>
                            <div>
                              <h3 className="font-semibold text-[#5ca88d] xl:text-lg">{serviceCopy.headings.expect}</h3>
                              <p className="mt-2 text-sm leading-relaxed text-gray-700 xl:text-base">{service.expect}</p>
                            </div>
                          </div>
                          <div className="mt-5 rounded-xl bg-[#d9f4cd] p-4 sm:p-5 xl:p-6">
                            <div className="flex flex-wrap gap-3">
                              <a
                                href={getServiceBookingHref(service.key)}
                                onClick={() => trackEvent("booking_cta_clicked", { language: lang, location: "service_page", service: service.key })}
                                className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#5ca88d] px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2"
                              >
                                {service.key === "glp1-support" ? "GLP-1 Programme" : serviceCopy.bookConsultation}
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function BeyondTheInjectionPage() {
  return (
    <main className="bg-white text-gray-900">
      <section className="bg-[#5ca88d] px-4 py-10 text-white sm:px-8 sm:py-14 lg:px-16 lg:py-20 xl:px-20">
        <div className="mx-auto grid max-w-screen-xl items-center gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(20rem,0.95fr)] lg:gap-12 xl:max-w-screen-2xl">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/90">Nutrition by Iballa</p>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl lg:whitespace-nowrap lg:text-6xl">
              Beyond the Injection
            </h1>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#5ca88d] shadow">12-week programme</span>
              <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#5ca88d] shadow">€399</span>
            </div>
            <div className="mt-8">
              <BeyondInjectionCta location="hero" variant="light">
                Learn more about the programme
              </BeyondInjectionCta>
            </div>
          </div>
          <div className="rounded-xl bg-white/20 p-3 shadow-lg ring-1 ring-white/40">
            <img
              src={beyondInjectionImage}
              alt="Beyond the Injection programme for GLP-1 nutrition support by registered dietitian Iballa"
              className="w-full rounded-lg object-cover shadow-md"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#ffb3b3] px-4 pt-12 pb-16 sm:px-8 sm:pt-16 sm:pb-20 lg:px-16 lg:pt-20 lg:pb-28 xl:px-20" aria-labelledby="beyond-included-heading">
        <div className="mx-auto max-w-screen-xl xl:max-w-screen-2xl">
          <div className="mx-auto mb-9 max-w-4xl text-center">
            <h2 id="beyond-included-heading" className="text-3xl font-semibold text-[#294b43] sm:text-4xl lg:text-5xl">
              What's included
            </h2>
            <p className="mt-3 text-base leading-relaxed text-[#294b43] sm:text-lg">
              A blend of education, one-to-one guidance, live support and community access across 12 weeks.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {beyondInclusions.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="rounded-xl bg-white p-5 shadow-lg ring-1 ring-[#ffb3b3]">
                  <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#d9f4cd] text-[#ffb3b3]">
                    <Icon size={24} aria-hidden="true" />
                  </span>
                  <h3 className="text-lg font-semibold leading-snug text-[#294b43]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-700">{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

function BlogOverview() {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).blog;
  const localizedPosts = localizeBlogPosts(blogPosts, i18n.language);
  const latestPost = localizedPosts[localizedPosts.length - 1];
  const postsNewestFirst = [...localizedPosts].reverse();

  return (
    <main className="bg-white text-gray-900">
      <section className="bg-[#5ca88d] pt-10 pb-10 text-center text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-sm font-semibold uppercase tracking-wide mb-3">{copy.eyebrow}</p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-tight">
            {copy.heading}
          </h1>
          <p className="mx-auto text-sm leading-relaxed sm:text-base md:whitespace-nowrap md:text-[clamp(0.875rem,1.6vw,1.125rem)]">
            {copy.intro}
          </p>
          <div className="mx-auto mt-6 h-2 w-28 rounded-full bg-[#ffb3b3]" aria-hidden="true"></div>
        </div>
      </section>

      <section className="relative bg-gray-50 pt-10 pb-8 sm:pt-14 sm:pb-10" aria-labelledby="latest-blog-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 id="latest-blog-heading" className="text-3xl font-semibold mb-6">
            {copy.latest}
          </h2>

          <div className="grid gap-6 lg:items-stretch">
            <div className="h-full">
              <article className="h-full rounded-xl shadow-lg ring-1 ring-[#5ca88d] overflow-hidden bg-white transition-all duration-200 hover:ring-2">
                <a
                  href={`/blog/${latestPost.slug}`}
                  className="grid h-full min-h-[26rem] group md:grid-cols-[minmax(18rem,0.95fr)_minmax(0,1.05fr)]"
                >
                  <img
                    src={latestPost.cardImage || latestPost.image}
                    alt=""
                    className="h-64 min-h-full w-full object-cover object-center md:h-full"
                  />
                  <div className="flex flex-col justify-center p-6 sm:p-8">
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-4">
                      <span className="inline-flex items-center rounded-full bg-[#d9f4cd] px-3 py-1 font-semibold text-[#5ca88d]">
                        {latestPost.category}
                      </span>
                      <span>{formatPostDate(latestPost.date, i18n.language)}</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={16} />
                        {latestPost.readingTime}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold leading-tight mb-4">
                      {latestPost.title}
                    </h3>
                    <p className="text-base sm:text-lg leading-relaxed text-gray-700 mb-6">
                      {latestPost.excerpt}
                    </p>
                    <span
                      className="self-start inline-flex items-center gap-2 bg-[#5ca88d] text-white text-sm font-semibold px-6 py-2 rounded-full shadow transition group-hover:opacity-90"
                    >
                      {copy.readMore}
                      <ArrowRight size={18} />
                    </span>
                  </div>
                </a>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10 sm:py-14" aria-labelledby="all-blog-posts-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 id="all-blog-posts-heading" className="text-3xl font-semibold mb-6">
            {copy.allPosts}
          </h2>
          <div className="space-y-5">
            {postsNewestFirst.map((post) => (
              <article key={post.slug} className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] overflow-hidden bg-white transition-all duration-200 hover:ring-2">
                <a href={`/blog/${post.slug}`} className="grid sm:grid-cols-[12rem_1fr] group">
                  <img src={post.cardImage || post.image} alt="" className="h-48 sm:h-full w-full object-cover object-center" />
                  <div className="p-5 sm:p-6">
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-4">
                      <span className="inline-flex items-center rounded-full bg-[#d9f4cd] px-3 py-1 font-semibold text-[#5ca88d]">
                        {post.category}
                      </span>
                      <span>{formatPostDate(post.date, i18n.language)}</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={16} />
                        {post.readingTime}
                      </span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold leading-tight mb-3 group-hover:text-[#5ca88d] transition">
                      {post.title}
                    </h3>
                    <p className="text-base leading-relaxed text-gray-700">
                      {post.excerpt}
                    </p>
                  </div>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}

function BlogSection({ section }) {
  const renderText = (text) => {
    if (!Array.isArray(text)) return text;

    return text.map((part) =>
      typeof part === "string" ? (
        part
      ) : (
        <a
          key={`${part.href}-${part.label}`}
          href={part.href}
          className="text-[#5ca88d] underline hover:text-[#5ca88d] transition"
        >
          {part.label}
        </a>
      )
    );
  };

  return (
    <section className="mb-10">
      <h2 className="text-2xl sm:text-3xl font-semibold mb-4 text-gray-900">{section.heading}</h2>
      {section.body?.map((paragraph) => (
        <p key={paragraph} className="text-base sm:text-lg leading-relaxed text-gray-700 mb-4">
          {renderText(paragraph)}
        </p>
      ))}
      {section.list?.length > 0 && (
        <ul className="list-disc pl-6 space-y-2 text-base sm:text-lg leading-relaxed text-gray-700 mb-4">
          {section.list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      {section.table && (
        <div className="overflow-x-auto rounded-xl shadow ring-1 ring-[#5ca88d] bg-white mb-4">
          <table className="min-w-full text-left text-sm sm:text-base">
            <thead className="bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] text-white">
              <tr>
                {section.table.headers.map((header) => (
                  <th key={header} scope="col" className="px-4 py-3 font-semibold">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#d9f4cd]">
              {section.table.rows.map((row) => (
                <tr key={row.join("-")} className="hover:bg-gray-50 transition">
                  {row.map((cell) => (
                    <td key={cell} className="px-4 py-3 text-gray-700">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {section.bodyAfter?.map((paragraph) => (
        <p key={paragraph} className="text-base sm:text-lg leading-relaxed text-gray-700 mb-4">
          {renderText(paragraph)}
        </p>
      ))}
      {section.subsections?.map((subsection) => (
        <div key={subsection.heading} className="mt-6">
          <h3 className="text-xl font-semibold mb-3 text-[#5ca88d]">{subsection.heading}</h3>
          {subsection.body?.map((paragraph) => (
            <p key={paragraph} className="text-base sm:text-lg leading-relaxed text-gray-700 mb-4">
              {renderText(paragraph)}
            </p>
          ))}
          {subsection.list && (
            <ul className="list-disc pl-6 space-y-2 text-base sm:text-lg leading-relaxed text-gray-700">
              {subsection.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </section>
  );
}

function BlogArticle({ post }) {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).blog;
  const localizedPosts = localizeBlogPosts(blogPosts, i18n.language);
  const currentIndex = localizedPosts.findIndex((item) => item.slug === post.slug);
  const previousPost = currentIndex > 0 ? localizedPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < localizedPosts.length - 1 ? localizedPosts[currentIndex + 1] : null;

  return (
    <main className="bg-white text-gray-900">
      <article>
        <div className="relative min-h-[18rem] sm:min-h-[24rem] overflow-hidden">
          <img src={post.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#5ca88d]/50"></div>
          <div className="relative z-10 min-h-[18rem] sm:min-h-[24rem] max-w-5xl mx-auto px-6 py-8 flex flex-col justify-center text-white">
            <a
              href="/blog"
              className="inline-flex items-center gap-2 mb-5 text-sm font-semibold hover:text-gray-100 transition"
            >
              <ArrowLeft size={18} />
              {copy.back}
            </a>
            <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 font-semibold text-[#5ca88d]">
                <Tag size={15} />
                {post.category}
              </span>
              <span>{formatPostDate(post.date, i18n.language)}</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={16} />
                {post.readingTime}
              </span>
              <span className="inline-flex items-center gap-1">
                <User size={16} />
                {post.author}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-4xl">
              {post.title}
            </h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          {post.callout && (
            <div className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-[#d9f4cd] text-[#5ca88d] p-5 sm:p-6 mb-10">
              <p className="text-base sm:text-lg font-semibold leading-relaxed">{post.callout}</p>
            </div>
          )}

          {post.sections.map((section) => (
            <BlogSection key={section.heading} section={section} />
          ))}

          {post.programmeBanner && <BeyondInjectionPromo embedded />}

          <section className="border-t border-[#d9f4cd] pt-8">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-4">{copy.references}</h2>
            <ol className="list-decimal pl-6 space-y-3 text-sm sm:text-base leading-relaxed text-gray-700">
              {post.references.map((reference) => (
                <li key={reference.url}>
                  <a
                    href={reference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#5ca88d] underline hover:text-[#5ca88d] transition"
                  >
                    {reference.label}
                  </a>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </article>

      <nav className="max-w-4xl mx-auto px-4 sm:px-6 pb-12 flex flex-col sm:flex-row gap-4 justify-between">
        {previousPost ? (
          <a
            href={`/blog/${previousPost.slug}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#5ca88d] shadow ring-1 ring-[#5ca88d] hover:bg-gray-100 transition"
          >
            <ArrowLeft size={18} />
            {copy.previous}
          </a>
        ) : (
          <span></span>
        )}
        {nextPost ? (
          <a
            href={`/blog/${nextPost.slug}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-5 py-2 text-sm font-semibold text-white shadow hover:brightness-105 transition"
          >
            {copy.next}
            <ArrowRight size={18} className="text-[#ffb3b3]" />
          </a>
        ) : (
          <a
            href="/blog"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-5 py-2 text-sm font-semibold text-white shadow hover:brightness-105 transition"
          >
            {copy.back}
            <ArrowRight size={18} className="text-[#ffb3b3]" />
          </a>
        )}
      </nav>
    </main>
  );
}

function AboutPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const testimonials = t("testimonials.items", { returnObjects: true });

  return (
    <main className="bg-white">
      <section className="bg-[#5ca88d] px-5 py-12 text-white sm:px-8 sm:py-16 lg:px-16 lg:py-20 xl:px-20">
        <div className="mx-auto flex max-w-screen-xl flex-col items-center gap-8 md:flex-row lg:gap-14 xl:max-w-screen-2xl xl:gap-20">
          <img
            src="/profile1.png"
            alt="Iballa Martinez"
            className="h-56 rounded-full border-4 border-white object-cover shadow-lg md:h-80 lg:h-[28rem] xl:h-[32rem]"
          />
          <div className="text-left">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-white/90">
              Nutrition by Iballa
            </p>
            <h1 className="mb-5 text-4xl font-semibold leading-tight lg:text-5xl">{t("about.heading")}</h1>
            <div className="space-y-4 text-[15px] leading-relaxed md:text-lg lg:text-xl">
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph2") }} />
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph3") }} />
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph4") }} />
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph5") }} />
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph6") }} />
            </div>
            <a
              href="/booking"
              onClick={() => trackEvent("booking_cta_clicked", { language: lang, location: "about_page" })}
              className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-[#5ca88d] shadow transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#5ca88d] sm:text-base"
            >
              {t("about.discoveryCta")}
              <ArrowRight size={18} className="text-[#ffb3b3]" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-12 sm:px-8 sm:py-16 lg:px-16 lg:py-20 xl:px-20" aria-labelledby="about-testimonials-heading">
        <div className="mx-auto max-w-screen-2xl">
          <div className="mx-auto mb-9 max-w-4xl text-center lg:mb-12">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#5ca88d]">
              Nutrition by Iballa
            </p>
            <h2 id="about-testimonials-heading" className="text-3xl font-semibold text-[#294b43] sm:text-4xl lg:text-5xl">
              {t("testimonials.aboutHeading")}
            </h2>
            <div className="mx-auto mt-5 h-2 w-24 rounded-full bg-[#ffb3b3]" aria-hidden="true"></div>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {testimonials.map((item) => (
              <article key={item.title} className="rounded-xl bg-white p-5 shadow-lg ring-2 ring-[#ffb3b3] sm:p-6">
                <h3 className="text-xl font-semibold leading-snug text-[#294b43]">{item.title}</h3>
                <div className="mt-4 space-y-4 text-sm leading-relaxed text-gray-700 sm:text-base">
                  {item.review.split("\n\n").map((paragraph) => (
                    <p key={paragraph}>&ldquo;{paragraph}&rdquo;</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function BookingPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const [bookingPolicyOpen, setBookingPolicyOpen] = useState(false);
  const types = [
    {
      key: "freeCall",
      icon: Clock,
      calendlyUrl: lang === "es" ? calendlyUrls.esFreeCall : calendlyUrls.enFreeCall,
      featured: true,
      duration: lang === "es" ? "15 minutos" : "15 minutes"
    },
    {
      key: "initial",
      icon: Calendar,
      calendlyUrl: lang === "es" ? "https://calendly.com/nutritionbyiballa/consulta-inicial" : "https://calendly.com/nutritionbyiballa/initial-consultation",
      duration: lang === "es" ? "55 minutos" : "55 minutes"
    },
    {
      key: "review",
      icon: RotateCcw,
      calendlyUrl: lang === "es" ? "https://calendly.com/nutritionbyiballa/consulta-seguimiento" : "https://calendly.com/nutritionbyiballa/review-consultation",
      duration: lang === "es" ? "30 minutos" : "30 minutes"
    }
  ];
  const packages = [
    {
      key: "bundle4",
      calendlyUrl: lang === "es" ? "https://calendly.com/nutritionbyiballa/bono-de-4-consultas" : "https://calendly.com/nutritionbyiballa/bundle-of-4-consultations"
    },
    {
      key: "bundle6",
      calendlyUrl: lang === "es" ? "https://calendly.com/nutritionbyiballa/bono-de-6-consultas" : "https://calendly.com/nutritionbyiballa/bundle-of-6-consultations"
    }
  ];
  const freeCall = types[0];
  const FreeCallIcon = freeCall.icon;
  const individualTypes = types.slice(1);
  const renderDescription = (description) => (
    <div className="space-y-3 text-base leading-relaxed text-gray-700">
      {Array.isArray(description) ? description.map((item) => <p key={item}>{item}</p>) : <p>{description}</p>}
    </div>
  );

  return (
    <main className="bg-white text-[#294b43]">
      <section className="bg-[#d9f4cd] px-5 py-10 sm:px-8 sm:py-12 lg:px-16 lg:py-14 xl:px-20">
        <div className="mx-auto max-w-screen-xl text-center">
          <h1 className="text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            {t("appointments.heading")}
          </h1>
          <div className="mx-auto mt-6 h-2 w-28 rounded-full bg-[#ffb3b3]" aria-hidden="true"></div>
        </div>
      </section>

      <section className="bg-white px-5 py-10 sm:px-8 sm:py-12 lg:px-16 lg:py-14 xl:px-20">
        <div className="mx-auto max-w-screen-xl space-y-10">
          <article className="overflow-hidden rounded-2xl bg-white shadow-[0_18px_45px_rgba(41,75,67,0.12)] ring-1 ring-[#5ca88d]">
            <div className="grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
              <div className="bg-[#5ca88d] p-6 text-white sm:p-8">
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20">
                    <FreeCallIcon size={25} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/85">
                      {t("appointments.recommended")}
                    </p>
                    <h2 className="text-2xl font-semibold leading-tight sm:text-3xl">
                      {t(`appointments.types.${freeCall.key}.title`)}
                    </h2>
                    <p className="mt-4 inline-flex rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#294b43]">
                      {freeCall.duration}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-between gap-6 p-6 sm:p-8">
                {renderDescription(t(`appointments.types.${freeCall.key}.description`, { returnObjects: true }))}
                <a
                  href={freeCall.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("appointment_type_selected", { language: lang, appointmentType: freeCall.key, location: "booking_page_featured" })}
                  className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-[#5ca88d] px-6 py-3 text-sm font-semibold text-white shadow transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2 sm:w-auto"
                >
                  {t(`appointments.types.${freeCall.key}.cta`, { defaultValue: t("appointments.cta") })}
                </a>
              </div>
            </div>
          </article>

          <section aria-labelledby="individual-consultations-heading">
            <h2 id="individual-consultations-heading" className="text-center text-2xl font-semibold text-[#294b43] sm:text-3xl">
              {t("appointments.individualHeading")}
            </h2>
            <div className="mt-6 grid items-stretch gap-5 md:grid-cols-2">
              {individualTypes.map((service) => {
                const Icon = service.icon;
                const desc = t(`appointments.types.${service.key}.description`, { returnObjects: true });
                return (
                  <article
                    key={service.key}
                    className="flex h-full flex-col rounded-2xl border border-[#d9f4cd] bg-white p-5 shadow-[0_10px_30px_rgba(41,75,67,0.08)] transition hover:border-[#5ca88d] sm:p-6"
                  >
                    <div className="grid grid-cols-[3.75rem_1fr] items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#d9f4cd] text-[#5ca88d]">
                        <Icon size={24} aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-xl font-semibold text-[#294b43]">{t(`appointments.types.${service.key}.title`)}</h3>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <p className="inline-flex rounded-full bg-[#d9f4cd] px-4 py-2 text-sm font-semibold text-[#294b43]">
                            {service.duration}
                          </p>
                          {service.key === "review" && (
                            <p className="inline-flex rounded-full bg-[#ffb3b3] px-3 py-2 text-xs font-semibold text-[#294b43]">
                              {t("appointments.existingClients")}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 flex flex-1 flex-col justify-between">
                      {renderDescription(desc)}
                      <a
                        href={service.calendlyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent("appointment_type_selected", { language: lang, appointmentType: service.key, location: "booking_page" })}
                        className="mt-6 inline-flex min-h-12 items-center justify-center rounded-full bg-[#5ca88d] px-6 py-3 text-sm font-semibold text-white shadow transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2"
                      >
                        {t(`appointments.types.${service.key}.cta`, { defaultValue: t("appointments.cta") })}
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="consultation-packages-heading">
            <h2 id="consultation-packages-heading" className="text-center text-2xl font-semibold text-[#294b43] sm:text-3xl">
              {t("appointments.packages.heading")}
            </h2>
            <div className="mt-6 grid items-stretch gap-5 md:grid-cols-2">
              {packages.map((pack) => {
                const copy = t(`appointments.packages.items.${pack.key}`, { returnObjects: true });
                return (
                  <article key={pack.key} className="flex h-full flex-col rounded-2xl border border-[#d9f4cd] bg-white p-5 shadow-[0_10px_30px_rgba(41,75,67,0.08)] transition hover:border-[#5ca88d] sm:p-6">
                    <div className="flex items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#d9f4cd] text-[#5ca88d]">
                        <CreditCard size={24} aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="text-xl font-semibold text-[#294b43]">{copy.title}</h3>
                      </div>
                    </div>
                    {Array.isArray(copy.includes) && (
                      <ul className="mt-5 space-y-2 text-sm leading-relaxed text-gray-700">
                        {copy.includes.map((item) => (
                          <li key={item} className="flex gap-2">
                            <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#5ca88d]" aria-hidden="true" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-5 rounded-xl bg-[#d9f4cd] p-4">
                      <p className="text-3xl font-semibold text-[#294b43]">{copy.price}</p>
                      <p className="mt-2 text-sm text-gray-700">
                        {t("appointments.packages.individualCost")}: <span className="font-semibold">{copy.individualCost}</span>
                      </p>
                      <p className="mt-2 inline-flex rounded-full bg-[#ffb3b3] px-3 py-1 text-sm font-semibold text-[#294b43]">
                        {t("appointments.packages.save")} {copy.saving}
                      </p>
                    </div>
                    <a
                      href={pack.calendlyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("appointment_package_selected", { language: lang, packageType: pack.key, location: "booking_page" })}
                      className="mt-auto inline-flex min-h-11 items-center justify-center rounded-full bg-[#5ca88d] px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2"
                    >
                      {t("appointments.packages.cta")}
                    </a>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="rounded-2xl bg-[#d9f4cd] p-5 shadow-sm ring-1 ring-[#d9f4cd] sm:p-6">
            <button
              type="button"
              aria-expanded={bookingPolicyOpen}
              aria-controls="booking-policy-details"
              onClick={() => setBookingPolicyOpen((open) => !open)}
              className="inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#5ca88d] bg-white px-4 py-2 text-left text-sm font-semibold text-[#294b43] transition hover:bg-[#d9f4cd] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d]"
            >
              <span>{t("appointments.policyHeading")}</span>
              {bookingPolicyOpen ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
            </button>
            <AnimatePresence initial={false}>
              {bookingPolicyOpen && (
                <motion.div
                  id="booking-policy-details"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ height: { duration: 0.22, ease: "easeOut" }, opacity: { duration: 0.16 } }}
                  className="overflow-hidden"
                >
                  <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-700">
                    {t("appointments.policy", { returnObjects: true }).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        </div>
      </section>
    </main>
  );
}

const assessmentIcons = {
  "basics": ClipboardList,
  "fruit-vegetables": Apple,
  "fibre-wholegrains": Wheat,
  protein: Utensils,
  hydration: GlassWater,
  "meal-patterns": Clock,
  lifestyle: Dumbbell
};

function AssessmentContactForm({ contact, onContactChange, onSubmit, submitting, error }) {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).assessment;
  return (
    <motion.div
      key="assessment-contact"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white overflow-hidden"
    >
      <div className="bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-5 py-6 text-white sm:px-8">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-white/90 p-3 text-[#5ca88d] shadow">
            <User size={28} />
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-semibold leading-tight">{copy.beforeTitle}</h2>
            <p className="mt-2 text-sm sm:text-base leading-relaxed text-white/95">
              {copy.beforeIntro}
            </p>
          </div>
        </div>
      </div>
      <form className="space-y-4 p-5 sm:p-8" onSubmit={onSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.firstName}</span>
            <input
              type="text"
              value={contact.firstName}
              onChange={(event) => onContactChange("firstName", event.target.value)}
              className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
              required
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.lastName}</span>
            <input
              type="text"
              value={contact.lastName}
              onChange={(event) => onContactChange("lastName", event.target.value)}
              className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
              required
            />
          </label>
        </div>
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.email}</span>
          <input
            type="email"
            value={contact.email}
            onChange={(event) => onContactChange("email", event.target.value)}
            className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
            required
          />
        </label>
        <p className="text-sm leading-relaxed text-gray-700">
          <span className="block font-semibold text-[#294b43]">
            {copy.consentTitle || copy.consentHeading}
          </span>
          <span className="mt-1 block">
            {copy.consentDescription || copy.consent}
          </span>
        </p>
        <NewsletterConsentChoice
          value={contact.newsletterOptIn}
          onChange={(value) => onContactChange("newsletterOptIn", value)}
          copy={copy}
          name="assessment-newsletter-consent"
        />
        {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? copy.saving : copy.start}
          <ArrowRight size={18} className="text-[#ffb3b3]" />
        </button>
      </form>
    </motion.div>
  );
}

function NutritionAssessmentPage() {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).assessment;
  const localizedSteps = localizeAssessmentSteps(assessmentSteps, i18n.language);
  const draft = loadAssessmentDraft();
  const initialContact = {
    firstName: "",
    lastName: "",
    email: "",
    ...(draft?.contact || {}),
    newsletterOptIn: true
  };
  const [contact, setContact] = useState(initialContact);
  const [leadId, setLeadId] = useState(draft?.leadId || "");
  const [contactSubmitted, setContactSubmitted] = useState(Boolean(draft?.leadId));
  const [answers, setAnswers] = useState(draft?.answers || {});
  const [currentStep, setCurrentStep] = useState(draft?.currentStep || 0);
  const [showResults, setShowResults] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [assessmentError, setAssessmentError] = useState("");
  const [savingResults, setSavingResults] = useState(false);
  const totalQuestions = getQuestionCount();
  const completedQuestions = getCompletedQuestionCount(answers);
  const progress = showResults
    ? 100
    : Math.round((completedQuestions / totalQuestions) * 100);
  const step = localizedSteps[currentStep];
  const StepIcon = assessmentIcons[step.id] || ClipboardList;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === localizedSteps.length - 1;
  const currentStepComplete = step.questions.every((question) => question.optional || answers[question.id]);

  useEffect(() => {
    if (!showResults) {
      saveAssessmentDraft({
        contact,
        leadId,
        answers,
        currentStep
      });
    }
  }, [answers, contact, currentStep, leadId, showResults]);

  const updateContact = (field, value) => {
    setContact((currentContact) => ({
      ...currentContact,
      [field]: value
    }));
  };

  const updateAnswer = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value
    }));
  };

  const submitContact = async (event) => {
    event.preventDefault();
    setContactError("");

    if (!contact.firstName.trim() || !contact.lastName.trim() || !isValidEmail(contact.email)) {
      setContactError(copy.detailsError);
      return;
    }

    setContactSubmitting(true);
    try {
      const { leadId: savedLeadId } = await createAssessmentLead(contact);
      setLeadId(savedLeadId);
      setContactSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setContactError(i18n.language.startsWith("es") ? getUiContent("es").common.genericError : error.message);
    } finally {
      setContactSubmitting(false);
    }
  };

  const goBack = () => {
    if (showResults) {
      setShowResults(false);
      setCurrentStep(localizedSteps.length - 1);
      return;
    }

    setCurrentStep((stepIndex) => Math.max(0, stepIndex - 1));
  };

  const goNext = async () => {
    if (!currentStepComplete) return;

    if (isLastStep) {
      const result = calculateAssessmentResults(answers, i18n.language);
      setAssessmentError("");
      setSavingResults(true);
      try {
        await saveAssessmentResponse({ leadId, answers, result });
        clearAssessmentDraft();
        setShowResults(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        setAssessmentError(i18n.language.startsWith("es") ? getUiContent("es").common.genericError : error.message);
      } finally {
        setSavingResults(false);
      }
      return;
    }

    setCurrentStep((stepIndex) => stepIndex + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const restartAssessment = () => {
    clearAssessmentDraft();
    setContact({
      firstName: "",
      lastName: "",
      email: "",
      newsletterOptIn: true
    });
    setLeadId("");
    setContactSubmitted(false);
    setAnswers({});
    setCurrentStep(0);
    setShowResults(false);
    setAssessmentError("");
    setContactError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="bg-white text-gray-900">
      <section className="pt-10 pb-10 text-center bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] text-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold uppercase tracking-wide mb-3">
            Nutrition by Iballa
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-tight">
            {copy.title}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            {copy.intro}
          </p>
        </div>
      </section>

      <section className="bg-gray-50 py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="mb-6 rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-[#5ca88d]">
                  {showResults ? copy.complete : contactSubmitted ? copy.step(currentStep + 1, localizedSteps.length) : copy.contactDetails}
                </p>
                <p className="text-sm text-gray-600">
                  {copy.disclaimer}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 self-start rounded-full bg-[#d9f4cd] px-3 py-1 text-sm font-semibold text-[#5ca88d]">
                <CheckCircle2 size={16} />
                {copy.percentComplete(progress)}
              </span>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {showResults ? (
              <motion.div
                key="assessment-results"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.25 }}
              >
                <AssessmentResults answers={answers} onBack={goBack} onRestart={restartAssessment} />
              </motion.div>
            ) : !contactSubmitted ? (
              <AssessmentContactForm
                contact={contact}
                onContactChange={updateContact}
                onSubmit={submitContact}
                submitting={contactSubmitting}
                error={contactError}
              />
            ) : (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.25 }}
                className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white overflow-hidden"
              >
                <div className="bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-5 py-6 text-white sm:px-8">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-white/90 p-3 text-[#5ca88d] shadow">
                      <StepIcon size={28} />
                    </div>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-semibold leading-tight">{step.title}</h2>
                      <p className="mt-2 text-sm sm:text-base leading-relaxed text-white/95">{step.intro}</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-8">
                  <div className="space-y-6">
                    {step.questions.map((question) => (
                      <fieldset key={question.id} className="space-y-3">
                        <legend className="text-base sm:text-lg font-semibold text-gray-900">
                          {question.label}
                          {question.helper && (
                            <span className="ml-2 text-sm font-normal text-gray-500">{question.helper}</span>
                          )}
                        </legend>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {question.options.map((option) => {
                            const selected = answers[question.id] === option.value;
                            return (
                              <label
                                key={option.value}
                                className={`flex min-h-16 cursor-pointer items-center gap-3 rounded-xl border p-4 text-sm sm:text-base transition ${
                                  selected
                                    ? "border-[#5ca88d] bg-[#d9f4cd] text-[#5ca88d] shadow"
                                    : "border-gray-200 bg-white text-gray-800 hover:border-[#5ca88d] hover:bg-gray-50"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={question.id}
                                  value={option.value}
                                  checked={selected}
                                  onChange={() => updateAnswer(question.id, option.value)}
                                  className="h-5 w-5 rounded-full border border-[#5ca88d] checked:bg-[#5ca88d] focus:ring-2 focus:ring-[#d9f4cd]"
                                />
                                <span className="font-medium leading-snug">{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </fieldset>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={isFirstStep}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-semibold text-[#5ca88d] shadow ring-1 ring-[#5ca88d] transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ArrowLeft size={18} />
                      {copy.back}
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      disabled={!currentStepComplete || savingResults}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {savingResults ? copy.saving : isLastStep ? copy.viewResults : copy.next}
                      <ArrowRight size={18} className="text-[#ffb3b3]" />
                    </button>
                  </div>
                  {assessmentError && (
                    <p className="mt-4 text-sm font-semibold text-red-600">{assessmentError}</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
}

function AssessmentResults({ answers, onBack, onRestart }) {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).assessment;
  const results = calculateAssessmentResults(answers, i18n.language);
  const positiveItems = results.positiveItems || (results.positive?.category ? [results.positive] : []);
  const improvementItems = results.improvementItems || (results.improvement?.category ? [results.improvement] : []);

  return (
    <div className="space-y-6">
      <section className="grid gap-4 md:grid-cols-2">
        <article className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 transition hover:ring-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-[#5ca88d]">
            {copy.positiveFocus}
          </p>
          {positiveItems.length > 0 ? (
            <div className="mt-3 space-y-4">
              {positiveItems.map((item) => (
                <div key={item.category}>
                  <h4 className="font-semibold text-[#294b43]">{item.category}</h4>
                  <p className="mt-2 whitespace-pre-line text-sm sm:text-base leading-relaxed text-gray-700">
                    {item.message}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-gray-700">
              {copy.noPositiveFocus}
            </p>
          )}
        </article>
        <article className="rounded-xl shadow-lg ring-1 ring-[#e7c978] bg-white p-5 transition hover:ring-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-[#8a6b17]">
            {copy.improvementFocus}
          </p>
          <div className="mt-3 space-y-4">
            {improvementItems.map((item) => (
              <div key={item.category}>
                <h4 className="font-semibold text-[#5f4a12]">{item.category}</h4>
                <p className="mt-2 whitespace-pre-line text-sm sm:text-base leading-relaxed text-gray-700">
                  {item.message}
                </p>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="rounded-xl shadow-lg bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] p-6 text-white sm:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h3 className="text-2xl sm:text-3xl font-semibold">{copy.tailoredHeading}</h3>
            <p className="mt-3 text-base sm:text-lg leading-relaxed">
              {copy.tailoredText}
            </p>
          </div>
          <a
            href="/booking"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#5ca88d] shadow transition hover:bg-gray-100"
          >
            {copy.book}
            <Calendar size={18} />
          </a>
        </div>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-semibold text-[#5ca88d] shadow ring-1 ring-[#5ca88d] transition hover:bg-gray-100"
        >
          <ArrowLeft size={18} />
          {copy.backAnswers}
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:brightness-105"
        >
          <RotateCcw size={18} />
          {copy.restart}
        </button>
      </div>
    </div>
  );
}

function AdminSubscribersPage() {
  const { i18n } = useTranslation();
  const copy = getUiContent(i18n.language).admin;
  const genericError = getUiContent(i18n.language).common.genericError;
  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);

  const loadAdminData = async () => {
    setLoadingSubscribers(true);
    setStatus({ type: "", message: "" });
    try {
      const admin = await isCurrentUserAdmin();
      setIsAdmin(admin);
      if (admin) {
        setSubscribers(await listSubscribers());
      }
    } catch (error) {
      setStatus({ type: "error", message: i18n.language.startsWith("es") ? genericError : error.message });
    } finally {
      setLoadingSubscribers(false);
    }
  };

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setCheckingSession(false);
      return undefined;
    }

    let mounted = true;

    getCurrentSession()
      .then((currentSession) => {
        if (!mounted) return;
        setSession(currentSession);
        setCheckingSession(false);
      })
      .catch((error) => {
        if (!mounted) return;
        setStatus({ type: "error", message: i18n.language.startsWith("es") ? genericError : error.message });
        setCheckingSession(false);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setIsAdmin(false);
      setSubscribers([]);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (session) {
      loadAdminData();
    }
  }, [session]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    try {
      await signInAdmin(form.email, form.password);
    } catch (error) {
      setStatus({ type: "error", message: i18n.language.startsWith("es") ? genericError : error.message });
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutAdmin();
      setSession(null);
      setIsAdmin(false);
      setSubscribers([]);
    } catch (error) {
      setStatus({ type: "error", message: i18n.language.startsWith("es") ? genericError : error.message });
    }
  };

  const toggleUnsubscribed = async (subscriber) => {
    setStatus({ type: "", message: "" });
    try {
      await updateUnsubscribeStatus(subscriber.id, !subscriber.unsubscribed);
      setSubscribers(await listSubscribers());
    } catch (error) {
      setStatus({ type: "error", message: i18n.language.startsWith("es") ? genericError : error.message });
    }
  };

  return (
    <main className="bg-white text-gray-900">
      <section className="pt-10 pb-10 text-center bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] text-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm font-semibold uppercase tracking-wide mb-3">{copy.eyebrow}</p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-tight">
            {copy.heading}
          </h1>
          <p className="text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            {copy.intro}
          </p>
        </div>
      </section>

      <section className="bg-gray-50 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          {!isSupabaseConfigured ? (
            <div className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900">{copy.notConfigured}</h2>
              <p className="mt-3 text-gray-700">
                {copy.notConfiguredText}
              </p>
            </div>
          ) : checkingSession ? (
            <div className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 sm:p-8">
              <p className="font-semibold text-[#5ca88d]">{copy.checking}</p>
            </div>
          ) : !session ? (
            <form className="mx-auto max-w-md rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 sm:p-8" onSubmit={handleLogin}>
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-full bg-[#d9f4cd] p-3 text-[#5ca88d]">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">{copy.signIn}</h2>
                  <p className="text-sm text-gray-600">{copy.signInHelp}</p>
                </div>
              </div>
              <div className="space-y-4">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.email}</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                    className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
                    required
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-gray-800">{copy.password}</span>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                    className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#d9f4cd]"
                    required
                  />
                </label>
                {status.message && <p className="text-sm font-semibold text-red-600">{status.message}</p>}
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:brightness-105"
                >
                  {copy.signInButton}
                  <ShieldCheck size={18} />
                </button>
              </div>
            </form>
          ) : !isAdmin ? (
            <div className="rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900">{copy.accessRequired}</h2>
              <p className="mt-3 text-gray-700">
                {copy.accessText}
              </p>
              {status.message && <p className="mt-3 text-sm font-semibold text-red-600">{status.message}</p>}
              <button
                type="button"
                onClick={handleSignOut}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-semibold text-[#5ca88d] shadow ring-1 ring-[#5ca88d] transition hover:bg-gray-100"
              >
                {copy.signOut}
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#5ca88d]">{copy.signedIn}</p>
                  <p className="text-sm text-gray-600">{session.user.email}</p>
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-semibold text-[#5ca88d] shadow ring-1 ring-[#5ca88d] transition hover:bg-gray-100"
                >
                  {copy.signOut}
                  <LogOut size={18} />
                </button>
              </div>

              {status.message && (
                <p className={`text-sm font-semibold ${status.type === "error" ? "text-red-600" : "text-[#5ca88d]"}`}>
                  {status.message}
                </p>
              )}

              <div className="overflow-hidden rounded-xl shadow-lg ring-1 ring-[#5ca88d] bg-white">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] text-white">
                      <tr>
                        {copy.columns.map((column) => (
                          <th key={column} className="px-4 py-3 font-semibold">{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#d9f4cd]">
                      {loadingSubscribers ? (
                        <tr>
                          <td colSpan="8" className="px-4 py-6 text-center text-gray-700">{copy.loading}</td>
                        </tr>
                      ) : subscribers.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-4 py-6 text-center text-gray-700">{copy.empty}</td>
                        </tr>
                      ) : (
                        subscribers.map((subscriber) => (
                          <tr key={subscriber.id} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-gray-800">
                              {subscriber.first_name || subscriber.last_name
                                ? `${subscriber.first_name || ""} ${subscriber.last_name || ""}`.trim()
                                : subscriber.name}
                            </td>
                            <td className="px-4 py-3 text-gray-800">{subscriber.email}</td>
                            <td className="px-4 py-3 text-gray-700">
                              {copy.sources[subscriber.source] || subscriber.source}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {subscriber.newsletter_opt_in ? copy.consented : copy.noConsent}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {formatPostDate(subscriber.signup_date, i18n.language)}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {subscriber.unsubscribed ? copy.unsubscribed : copy.subscribed}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {copy.providerStatuses[subscriber.provider_sync?.status || "not_configured"]
                                || subscriber.provider_sync?.status
                                || copy.providerStatuses.not_configured}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => toggleUnsubscribed(subscriber)}
                                className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-[#5ca88d] shadow ring-1 ring-[#5ca88d] transition hover:bg-gray-100"
                              >
                                {subscriber.unsubscribed ? copy.markSubscribed : copy.markUnsubscribed}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default function NutritionByIballa() {
  const [state, handleSubmit] = useForm("xzzvqdlq");
  const [messageValue, setMessageValue] = useState("");
  const messageRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const uiCopy = getUiContent(lang);
  const localizedBlogPosts = localizeBlogPosts(blogPosts, lang);
  const [expandedServiceKey, setExpandedServiceKey] = useState(null);
  const [path, setPath] = useState(window.location.pathname);

  const slug = path.startsWith("/blog/") ? path.replace("/blog/", "") : "";
  const activePost = slug ? localizedBlogPosts.find((post) => post.slug === slug) || getBlogPost(slug) : null;
  const isAboutRoute = path === "/about";
  const isServicesRoute = path === "/services";
  const isBlogRoute = path === "/blog";
  const isArticleRoute = path.startsWith("/blog/");
  const isBookingRoute = path === "/booking";
  const isAssessmentRoute = path === "/nutrition-assessment";
  const isAdminSubscribersRoute = path === "/admin/subscribers";
  const isPrivacyRoute = path === "/privacy-policy";
  const isCookieRoute = path === "/cookie-policy";
  const isBeyondInjectionRoute = path === "/beyond-the-injection";
  const serviceCopy = servicesContent[lang] || servicesContent.en;

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const handleInternalNavigation = (event) => {
      const anchor = event.target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

      if (
        !href ||
        isModifiedClick ||
        anchor.target === "_blank" ||
        (!href.startsWith("/about") && !href.startsWith("/services") && !href.startsWith("/booking") && !href.startsWith("/blog") && !href.startsWith("/nutrition-assessment") && !href.startsWith("/beyond-the-injection") && !href.startsWith("/privacy-policy") && !href.startsWith("/cookie-policy") && !href.startsWith("/admin/subscribers") && !href.startsWith("/#"))
      ) {
        return;
      }

      event.preventDefault();
      window.history.pushState({}, "", href);
      setPath(window.location.pathname);

      window.setTimeout(() => {
        if (window.location.hash) {
          document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 0);
    };

    document.addEventListener("click", handleInternalNavigation);
    return () => document.removeEventListener("click", handleInternalNavigation);
  }, []);

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.style.height = "auto";
      messageRef.current.style.height = `${messageRef.current.scrollHeight}px`;
    }
  }, [messageValue]);

  useEffect(() => {
    const widgetContainer = document.getElementById("calendly-widget");
    const alreadyLoaded = widgetContainer?.querySelector("iframe");

    if (window.Calendly && widgetContainer && !alreadyLoaded) {
      window.Calendly.initInlineWidget({
        url: "https://calendly.com/nutritionbyiballa",
        parentElement: widgetContainer,
        prefill: {},
        utm: {}
      });
    }
  }, []);

  useEffect(() => {
    const title = activePost
      ? `${activePost.title} | Nutrition by Iballa`
      : isAboutRoute
        ? `${t("nav.about")} | Nutrition by Iballa`
      : isServicesRoute
        ? `${t("nav.services")} | Nutrition by Iballa`
      : isBookingRoute
        ? `${t("nav.bookNow")} | Nutrition by Iballa`
      : isBeyondInjectionRoute
        ? "Beyond the Injection | GLP-1 Nutrition Programme | Nutrition by Iballa"
      : isAssessmentRoute
        ? `${uiCopy.assessment.title} | Nutrition by Iballa`
      : isAdminSubscribersRoute
        ? `${uiCopy.admin.heading} | Nutrition by Iballa`
      : isPrivacyRoute
        ? `${i18n.language.startsWith("es") ? "Política de privacidad" : "Privacy Policy"} | Nutrition by Iballa`
      : isCookieRoute
        ? `${i18n.language.startsWith("es") ? "Política de cookies" : "Cookie Policy"} | Nutrition by Iballa`
      : isBlogRoute
        ? "Blog | Nutrition by Iballa"
        : "Nutrition by Iballa";
    const description = activePost
      ? activePost.excerpt
      : isAboutRoute
        ? t("about.paragraph1").replace(/<[^>]+>/g, "")
      : isServicesRoute
        ? serviceCopy.intro
      : isBookingRoute
        ? t("appointments.mealPlanNote")
      : isBeyondInjectionRoute
        ? "Beyond the Injection is a 12-week GLP-1 nutrition programme from Nutrition by Iballa, providing structured nutrition education, practical guidance and personalised dietetic support."
      : isAssessmentRoute
        ? uiCopy.assessment.intro
      : isAdminSubscribersRoute
        ? uiCopy.admin.intro
      : isPrivacyRoute
        ? i18n.language.startsWith("es")
          ? "Política de privacidad de Nutrition by Iballa."
          : "Privacy Policy for Nutrition by Iballa."
      : isCookieRoute
        ? i18n.language.startsWith("es")
          ? "Política de cookies de Nutrition by Iballa."
          : "Cookie Policy for Nutrition by Iballa."
      : isBlogRoute
        ? uiCopy.blog.intro
        : t("hero.subtitle");

    document.title = title;
    upsertMeta("description", description);
    upsertMeta("robots", isAdminSubscribersRoute ? "noindex, nofollow" : "index, follow");
    upsertPropertyMeta("og:title", title);
    upsertPropertyMeta("og:description", description);
    upsertPropertyMeta("og:type", activePost ? "article" : "website");
    upsertPropertyMeta("og:image", `${window.location.origin}${isBeyondInjectionRoute ? beyondInjectionImage : activePost?.image || "/logo.png"}`);
    upsertPropertyMeta("og:url", `${window.location.origin}${activePost ? `/blog/${activePost.slug}` : isAboutRoute ? "/about" : isServicesRoute ? "/services" : isBookingRoute ? "/booking" : isBeyondInjectionRoute ? "/beyond-the-injection" : isAdminSubscribersRoute ? "/admin/subscribers" : isCookieRoute ? "/cookie-policy" : isPrivacyRoute ? "/privacy-policy" : isAssessmentRoute ? "/nutrition-assessment" : isBlogRoute ? "/blog" : "/"}`);
    upsertCanonical(activePost ? `/blog/${activePost.slug}` : isAboutRoute ? "/about" : isServicesRoute ? "/services" : isBookingRoute ? "/booking" : isBeyondInjectionRoute ? "/beyond-the-injection" : isAdminSubscribersRoute ? "/admin/subscribers" : isCookieRoute ? "/cookie-policy" : isPrivacyRoute ? "/privacy-policy" : isAssessmentRoute ? "/nutrition-assessment" : isBlogRoute ? "/blog" : "/");
    upsertStructuredData(
      activePost
        ? {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: activePost.title,
            description: activePost.excerpt,
            image: `${window.location.origin}${activePost.image}`,
            datePublished: activePost.date,
            author: {
              "@type": "Person",
              name: activePost.author
            },
            publisher: {
              "@type": "Organization",
              name: "Nutrition by Iballa"
            },
            mainEntityOfPage: `${window.location.origin}/blog/${activePost.slug}`
          }
        : isAboutRoute
          ? {
              "@context": "https://schema.org",
              "@type": "AboutPage",
              name: `${t("nav.about")} | Nutrition by Iballa`,
              description,
              url: `${window.location.origin}/about`
            }
        : isServicesRoute
          ? {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: `${t("nav.services")} | Nutrition by Iballa`,
              description,
              url: `${window.location.origin}/services`
            }
        : isBookingRoute
          ? {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: `${t("nav.bookNow")} | Nutrition by Iballa`,
              description,
              url: `${window.location.origin}/booking`
            }
        : isBeyondInjectionRoute
          ? {
              "@context": "https://schema.org",
              "@type": "Course",
              name: "Beyond the Injection",
              description,
              image: `${window.location.origin}${beyondInjectionImage}`,
              provider: {
                "@type": "Organization",
                name: "Nutrition by Iballa",
                url: window.location.origin
              },
              offers: {
                "@type": "Offer",
                price: "399",
                priceCurrency: "EUR",
                url: beyondInjectionUrl,
                availability: "https://schema.org/InStock"
              }
            }
        : isAssessmentRoute
          ? {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: uiCopy.assessment.title,
            description,
            url: `${window.location.origin}/nutrition-assessment`
          }
        : isAdminSubscribersRoute
          ? {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: "Admin Subscribers",
              description,
              url: `${window.location.origin}/admin/subscribers`
            }
        : isPrivacyRoute
          ? {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: i18n.language.startsWith("es") ? "Política de privacidad" : "Privacy Policy",
              description,
              url: `${window.location.origin}/privacy-policy`
            }
        : isCookieRoute
          ? {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: i18n.language.startsWith("es") ? "Política de cookies" : "Cookie Policy",
              description,
              url: `${window.location.origin}/cookie-policy`
            }
        : isBlogRoute
          ? {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Nutrition by Iballa Blog",
            description,
            url: `${window.location.origin}/blog`
          }
          : {
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "Nutrition by Iballa",
              description,
              url: window.location.origin
            }
    );
  }, [activePost, i18n.language, isAboutRoute, isAdminSubscribersRoute, isAssessmentRoute, isBeyondInjectionRoute, isBlogRoute, isBookingRoute, isCookieRoute, isPrivacyRoute, isServicesRoute, serviceCopy.intro, t, uiCopy]);

  return (
   <div className="overflow-x-hidden bg-white text-gray-900 font-sans">
    {/* Header */}
  <header className="sticky top-0 h-24 border-b-2 border-[#ffb3b3] bg-white z-[999]">
  <div className="mx-auto flex h-full w-full max-w-screen-2xl items-center justify-between gap-3 bg-white px-3 sm:px-4 lg:px-10 relative z-[1000]">
    <a href="/" aria-label={t("nav.home")} className="inline-flex shrink-0">
      <img src="/logo.png" alt={t("logoAlt")} className="h-16 w-[140px] -translate-y-1 object-contain sm:h-20 sm:w-[180px] lg:h-24 lg:w-[210px]" />
    </a>

    <div className="flex shrink-0 items-center gap-2 sm:gap-4 md:gap-6 relative z-[1000]">
      <LanguageDropdown />
      <nav className="hidden md:flex items-center space-x-6 lg:text-base xl:text-lg">
        <a href="/about">{t("nav.about")}</a>
        <a href="/services">{t("nav.services")}</a>
        <a href="/beyond-the-injection">{t("nav.glp1")}</a>
        <a href="/blog">{t("nav.blog")}</a>
        <a href="/#contact">{t("nav.contact")}</a>
        <a
          href="/booking"
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2 xl:text-base"
        >
          {t("nav.bookNow")}
        </a>
      </nav>

      {/* Mobile Toggle */}
      <button
        className="md:hidden relative z-[1100] text-gray-900"
        onClick={() => setNavOpen(!navOpen)}
      >
        {navOpen ? <X size={28} /> : <Menu size={28} />}
      </button>
    </div>
  </div>

  {/* Mobile Navigation */}
  {navOpen && (
    <nav className="absolute top-24 left-0 w-full border-b-2 border-[#ffb3b3] bg-white shadow-md flex flex-col items-center space-y-4 p-6 z-60 md:hidden">
      <a href="/about" onClick={() => setNavOpen(false)}>
        {t("nav.about")}
      </a>
      <a href="/services" onClick={() => setNavOpen(false)}>
        {t("nav.services")}
      </a>
      <a href="/beyond-the-injection" onClick={() => setNavOpen(false)}>
        {t("nav.glp1")}
      </a>
      <a href="/blog" onClick={() => setNavOpen(false)}>
        {t("nav.blog")}
      </a>
      <a href="/#contact" onClick={() => setNavOpen(false)}>
        {t("nav.contact")}
      </a>
      <a
        href="/booking"
        onClick={() => setNavOpen(false)}
        className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow transition hover:opacity-90"
      >
        {t("nav.bookNow")}
      </a>
    </nav>
  )}
</header>


{isBlogRoute ? (
  <BlogOverview />
) : isAboutRoute ? (
  <AboutPage />
) : isServicesRoute ? (
  <ServicesPage
    title={t("nav.services")}
    serviceCopy={serviceCopy}
    expandedServiceKey={expandedServiceKey}
    setExpandedServiceKey={setExpandedServiceKey}
    lang={lang}
  />
) : isBookingRoute ? (
  <BookingPage />
) : isAssessmentRoute ? (
  <NutritionAssessmentPage />
) : isAdminSubscribersRoute ? (
  <AdminSubscribersPage />
) : isPrivacyRoute ? (
  <PrivacyPolicyPage />
) : isCookieRoute ? (
  <CookiePolicyPage />
) : isBeyondInjectionRoute ? (
  <BeyondTheInjectionPage />
) : isArticleRoute && activePost ? (
  <BlogArticle post={activePost} />
) : isArticleRoute ? (
  <main className="bg-gray-50 px-4 py-16 text-center">
    <h1 className="text-3xl font-semibold mb-4">{uiCopy.blog.notFound}</h1>
    <p className="text-gray-700 mb-6">{uiCopy.blog.notFoundText}</p>
    <a
      href="/blog"
      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#d9f4cd] to-[#5ca88d] px-6 py-2 text-sm font-semibold text-white shadow hover:brightness-105 transition"
    >
      <ArrowLeft size={18} />
      {uiCopy.blog.back}
    </a>
  </main>
) : (
<>
{/* Hero Section */}
<section className="bg-white text-[#294b43]">
  <div className="mx-auto grid w-full max-w-screen-2xl items-center gap-8 px-5 pb-0 pt-8 sm:px-8 md:px-12 lg:min-h-[38rem] lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)] lg:px-16 xl:px-20">
    <div className="z-10 w-full max-w-3xl text-left md:justify-self-start">
      <h1 className="max-w-full text-4xl font-semibold leading-[1.15] text-[#294b43] sm:text-5xl lg:text-[clamp(1.75rem,3vw,3rem)]">
        {lang === "en" ? t("hero.subtitleLines", { returnObjects: true }).map((line) => (
          <span key={line} className="lg:block lg:whitespace-nowrap">{line}{" "}</span>
        )) : t("hero.subtitle")}
      </h1>
      <p className="mt-6 max-w-[38rem] text-base leading-relaxed text-[#0f2f2b] sm:text-lg lg:text-xl">
        {t("hero.supporting")}
      </p>
      <a
        href="/nutrition-assessment"
        onClick={() => trackEvent("nutrition_assessment_clicked", { language: lang, location: "hero_assessment" })}
        className="mt-8 inline-flex min-h-14 items-center justify-center gap-3 rounded-full bg-[#5ca88d] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#294b43]/15 transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2 sm:px-10 sm:text-lg"
      >
        <ClipboardList size={22} aria-hidden="true" />
        {t("hero.assessmentCta")}
      </a>
    </div>

    <div className="flex items-end justify-center self-end overflow-hidden">
      <img
        src="/hero-iballa-cutout-transparent.png"
        alt="Iballa Martinez, registered dietitian"
        className="max-h-[28rem] w-auto max-w-full object-contain object-bottom drop-shadow-[0_24px_34px_rgba(41,75,67,0.16)] lg:max-h-[38rem]"
      />
    </div>
  </div>
</section>

<section className="bg-[#d9f4cd] px-5 py-10 text-[#294b43] sm:px-8 sm:py-12 lg:px-16 lg:py-14 xl:px-20">
  <div className="mx-auto max-w-4xl text-center">
    <div>
      <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#5ca88d]">
        {t("hero.discoveryEyebrow")}
      </p>
      <h2 className="text-3xl font-semibold leading-[1.15] text-[#294b43] sm:text-4xl lg:text-5xl">
        {t("hero.discoveryTitle")}
      </h2>
      <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-[#0f2f2b] sm:text-lg lg:text-xl">
        {t("hero.discoveryText")}
      </p>
      <a
        href={lang === "es" ? calendlyUrls.esFreeCall : calendlyUrls.enFreeCall}
        onClick={() => trackEvent("booking_cta_clicked", { language: lang, location: "hero_discovery_card" })}
        className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#5ca88d] px-6 py-3 text-sm font-semibold text-white shadow transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ca88d] focus-visible:ring-offset-2"
      >
        <Calendar size={18} aria-hidden="true" />
        {t("hero.cta")}
      </a>
    </div>
  </div>
</section>


<BeyondInjectionPromo />

<section className="bg-white px-5 py-12 sm:px-8 sm:py-16 lg:px-16 lg:py-20 xl:px-20" aria-labelledby="testimonials-heading">
  <div className="mx-auto max-w-screen-2xl">
    <div className="mx-auto mb-9 max-w-4xl text-center lg:mb-12">
      <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#5ca88d]">
        Nutrition by Iballa
      </p>
      <h2 id="testimonials-heading" className="text-3xl font-semibold text-[#294b43] sm:text-4xl lg:text-5xl">
        {t("testimonials.heading")}
      </h2>
    </div>
    <div className="grid gap-5 lg:grid-cols-3 xl:gap-8">
      {t("testimonials.items", { returnObjects: true }).map((item, index) => (
        <article key={item.title} className="flex h-full flex-col rounded-xl bg-white p-5 shadow-lg ring-1 ring-[#d9f4cd] sm:p-6">
          <h3 className={`text-xl font-semibold leading-snug text-[#294b43] ${index === 0 && lang === "en" ? "lg:text-[clamp(1rem,1.35vw,1.25rem)]" : "sm:text-2xl"}`}>
            {index === 0 && lang === "en" ? (
              <><span className="lg:block lg:whitespace-nowrap">She made me feel heard,</span>{" "}<span className="lg:block lg:whitespace-nowrap">understood, and supported</span></>
            ) : item.title}
          </h3>
          <blockquote className="mt-5 border-l-4 border-[#ffb3b3] pl-4 text-sm leading-relaxed text-gray-700 sm:text-base">
            {item.review.split("\n\n").map((paragraph) => (
              <p key={paragraph} className="mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </blockquote>
        </article>
      ))}
    </div>
  </div>
</section>

     {/* Contact */}
<section id="contact" className="bg-[#ffb3b3] p-8 text-center text-[#294b43] sm:p-12 lg:px-16 lg:py-20 xl:px-20">
  <h2 className="text-3xl font-semibold mb-4 lg:text-5xl">{t("contact.heading")}</h2>

  <p
    className="text-base sm:text-lg lg:text-xl max-w-xl lg:max-w-3xl mx-auto mb-6 lg:mb-8"
    dangerouslySetInnerHTML={{ __html: t("contact.intro") }}
  />

  {state.succeeded ? (
    <p className="text-lg text-[#294b43]">{t("contact.success")}</p>
  ) : (
    <form className="max-w-xl lg:max-w-3xl mx-auto w-full space-y-4 lg:space-y-5" onSubmit={handleSubmit}>
      {/* Name */}
      <input
        id="name"
        name="name"
        required
        placeholder={t("contact.fields.name")}
        className="w-full h-10 lg:h-14 rounded-md border border-gray-300 bg-white px-3 lg:px-5 lg:text-lg text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
      />

      {/* Email */}
      <input
        id="email"
        name="email"
        type="email"
        required
        placeholder={t("contact.fields.email")}
        className="w-full h-10 lg:h-14 rounded-md border border-gray-300 bg-white px-3 lg:px-5 lg:text-lg text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
      />

      <input
        id="mobile"
        name="mobile"
        type="tel"
        required
        autoComplete="tel"
        placeholder={t("contact.fields.mobile")}
        className="w-full h-10 lg:h-14 rounded-md border border-gray-300 bg-white px-3 lg:px-5 lg:text-lg text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
      />

      {/* Message */}
      <textarea
        id="message"
        name="message"
        required
        rows={4}
        ref={messageRef}
        value={messageValue}
        onChange={(e) => setMessageValue(e.target.value)}
        placeholder={t("contact.fields.message")}
        className="w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 lg:px-5 lg:py-4 lg:text-lg text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
      />

      <p className="text-sm text-[#294b43]/75 lg:text-base">{t("contact.fields.note")}</p>

      {/* Submit */}
      <div className="text-center pt-2">
        <button
          type="submit"
          disabled={state.submitting}
          className="cursor-pointer rounded-md bg-white px-8 py-3 text-sm font-medium text-green-700 transition-colors duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-not-allowed disabled:opacity-60 lg:px-10 lg:py-4 lg:text-base"
        >
          {state.submitting ? t("contact.fields.sending") : t("contact.fields.submit")}
        </button>
      </div>
    </form>
  )}
</section>

      </>
)}

      {!isBlogRoute && !isAssessmentRoute && !isAdminSubscribersRoute && !isPrivacyRoute && !isArticleRoute && (
        <FooterNewsletterSignup />
      )}

      {/* Footer */}
      <footer className="bg-white text-[#1e1e5a] px-4 py-6 lg:px-12 lg:py-8 rounded-t-lg shadow-inner">
        <div className="mx-auto flex w-full max-w-screen-2xl flex-col items-center gap-4 text-center">
          <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
            {/* Brand */}
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
              <img
                src="/favicon.png"
                alt={t("footer.logoAlt")}
                className="h-12 w-12 rounded-full object-cover sm:h-10 sm:w-10 lg:h-12 lg:w-12"
              />
              <p className="text-sm font-semibold leading-tight text-[#294b43] sm:text-xs lg:text-sm">
                {t("footer.bio")}
              </p>
            </div>

            {/* Social Handles */}
            <div className="flex flex-col items-center gap-2 text-xs font-semibold text-[#1e1e5a] sm:flex-row sm:gap-4 lg:text-sm">
              <a
                href="https://linkedin.com/in/iballamartinezyanes"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn: Iballa Martinez Yanes"
                title="LinkedIn: Iballa Martinez Yanes"
                className="inline-flex items-center gap-2 hover:opacity-80 transition duration-200"
              >
                <Linkedin size={20} color="#1e1e5a" />
                <span>iballamartinezyanes</span>
              </a>
              <a
                href="https://www.instagram.com/thespanishdietitian?igsh=NWYzbjF3ZmNsczdp&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram: @thespanishdietitian"
                title="Instagram: @thespanishdietitian"
                className="inline-flex items-center gap-2 hover:opacity-80 transition duration-200"
              >
                <Instagram size={20} color="#1e1e5a" />
                <span>@thespanishdietitian</span>
              </a>
              <a
                href="https://www.tiktok.com/@thespanishdietitian"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok: @thespanishdietitian"
                title="TikTok: @thespanishdietitian"
                className="inline-flex items-center gap-2 hover:opacity-80 transition duration-200"
              >
                <Music2 size={20} color="#1e1e5a" />
                <span>@thespanishdietitian</span>
              </a>
            </div>
          </div>

          {/* Policies */}
          <div className="flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-[#d9f4cd] pt-3 text-[11px] font-semibold lg:text-xs">
            <a
              href="/privacy-policy"
              className="text-[#5ca88d] underline underline-offset-2 hover:text-[#5ca88d]"
            >
              {t("footer.privacyPolicy")}
            </a>
            <a
              href="/cookie-policy"
              className="text-[#5ca88d] underline underline-offset-2 hover:text-[#5ca88d]"
            >
              {t("footer.cookiePolicy")}
            </a>
          </div>
        </div>
      </footer>
     <Analytics />
    </div>
  );
}




